import React, { useEffect, useMemo, useState, useRef } from "react";
import type { ArtworkCurrentStatusDto } from "../../models/ArtworkStatus";
import { createApiClient } from "../useApiClient";
import { useUser } from "../UserContext";
import { useArtworkStatus } from "../../functions/useArtworkStatus";
import '../ArtworkStatus/ArtworkStatusStyling.css';
import { Typography, Button, Divider, Box, Paper, IconButton, Tooltip } from "@mui/material";
import DescriptionIcon from '@mui/icons-material/Description';
import { useReferenceData } from '../../components/ReferenceDataContext';
import StatusViewer from './StatusViewer';

import { ThemeProvider } from "@mui/material/styles";
import { theme } from "../../styles/mui_theme";

import type { RefreshTriggers } from "../../models/ArtworkDataTriggers";

export type SetStatusRequest = {
    statusCode: string;
    notes?: string | null;
    whenUtc?: string | null;
    additionalData?: Record<string, any>;
};

export type ArtworkStatusPanelProps = {
    artworkId: number;
    onStatusChanged?: (current: ArtworkCurrentStatusDto) => void;
    groupByCategory?: boolean;
    refresh: RefreshTriggers;
};

// ===== Types =====
type ValueFormData = {
    who: string;
    date: string;
    value: number | null;
    currency: string;
};

type OutFormData = {
    to: string;
    dateFrom: string;
    dateTo: string;
};

type FromFormData = {
    fromWho: string;
    dateExpected: string;
    dateToReturn: string;
};

type AuctionFormData = {
    auctionHouse: string;
    proposedSaleDate: string;
    lotNumber: string;
    title: string;
    reservePrice: number | null;
    currency: string;
};

type AdditionalFormData = ValueFormData | OutFormData | FromFormData | AuctionFormData | null;


// ===== Form Components =====
const ValueForm: React.FC<{
    data: ValueFormData;
    onChange: (data: ValueFormData) => void;
}> = ({ data, onChange }) => {
    const { currencies } = useReferenceData();


    return (
        <div className="extended-form value-form">
            <div className="form-header">
                <div className="form-icon">💰</div>
                <div>
                    <h4 className="form-title">Valuation Details</h4>
                    <p className="form-subtitle">Required for valuation status</p>
                </div>
            </div>

            <div className="form-grid">
                <div>
                    <label className="form-label">
                        Valuation By <span className="required">*</span>
                    </label>
                    <input
                        type="text"
                        value={data.who}
                        onChange={(e) => onChange({ ...data, who: e.target.value })}
                        placeholder="Enter appraiser name"
                        className="form-input"
                        required
                    />
                </div>

                <div>
                    <label className="form-label">
                        Valuation Date <span className="required">*</span>
                    </label>
                    <input
                        type="date"
                        value={data.date}
                        onChange={(e) => onChange({ ...data, date: e.target.value })}
                        className="form-input"
                        required
                    />
                </div>

                <div>
                    <label className="form-label">
                        Value <span className="required">*</span>
                    </label>
                    <div className="input-with-prefix">
                        <span className="input-prefix">~</span>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={data.value || ''}
                            onChange={(e) => onChange({ ...data, value: e.target.value ? parseFloat(e.target.value) : null })}
                            placeholder="0.00"
                            className="form-input"
                            required
                        />
                    </div>
                </div>

                <div>
                    <label className="form-label">
                        Currency <span className="required">*</span>
                    </label>
                    <select
                        value={data.currency}
                        onChange={(e) => onChange({ ...data, currency: e.target.value })}
                        className="form-input"
                        required
                    >
                        {currencies?.map((curr: any) => (
                            <option key={curr.currencyCode} value={curr.currencyCode}>
                                {curr.currencyCode}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );
};

const OutForm: React.FC<{
    data: OutFormData;
    onChange: (data: OutFormData) => void;
}> = ({ data, onChange }) => {
    return (
        <div className="extended-form out-form">
            <div className="form-header">
                <div className="form-icon">📤</div>
                <div>
                    <h4 className="form-title">Location Out Details</h4>
                    <p className="form-subtitle">Required for outgoing artwork</p>
                </div>
            </div>

            <div className="form-stack">
                <div>
                    <label className="form-label">
                        To (Location/Person) <span className="required">*</span>
                    </label>
                    <input
                        type="text"
                        value={data.to}
                        onChange={(e) => onChange({ ...data, to: e.target.value })}
                        placeholder="Gallery, client name, exhibition, etc."
                        className="form-input"
                        required
                    />
                </div>

                <div className="form-grid">
                    <div>
                        <label className="form-label">
                            Date From <span className="required">*</span>
                        </label>
                        <input
                            type="date"
                            value={data.dateFrom}
                            onChange={(e) => onChange({ ...data, dateFrom: e.target.value })}
                            className="form-input"
                            required
                        />
                    </div>

                    <div>
                        <label className="form-label">
                            Date To <span className="required">*</span>
                        </label>
                        <input
                            type="date"
                            value={data.dateTo}
                            onChange={(e) => onChange({ ...data, dateTo: e.target.value })}
                            min={data.dateFrom}
                            className="form-input"
                            required
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

const FromForm: React.FC<{
    data: FromFormData;
    onChange: (data: FromFormData) => void;
}> = ({ data, onChange }) => {
    return (
        <div className="extended-form from-form">
            <div className="form-header">
                <div className="form-icon">📥</div>
                <div>
                    <h4 className="form-title">Location From Details</h4>
                    <p className="form-subtitle">Required for incoming artwork</p>
                </div>
            </div>

            <div className="form-stack">
                <div>
                    <label className="form-label">
                        From (Location/Person) <span className="required">*</span>
                    </label>
                    <input
                        type="text"
                        value={data.fromWho}
                        onChange={(e) => onChange({ ...data, fromWho: e.target.value })}
                        placeholder="Lender, gallery, collector, etc."
                        className="form-input"
                        required
                    />
                </div>

                <div className="form-grid">
                    <div>
                        <label className="form-label">
                            Date Expected <span className="required">*</span>
                        </label>
                        <input
                            type="date"
                            value={data.dateExpected}
                            onChange={(e) => onChange({ ...data, dateExpected: e.target.value })}
                            className="form-input"
                            required
                        />
                    </div>

                    <div>
                        <label className="form-label">
                            Date to Return <span className="required">*</span>
                        </label>
                        <input
                            type="date"
                            value={data.dateToReturn}
                            onChange={(e) => onChange({ ...data, dateToReturn: e.target.value })}
                            min={data.dateExpected}
                            className="form-input"
                            required
                        />
                    </div>
                </div>
            </div>
        </div>
    );
    };


const AuctionForm: React.FC<{
    data: AuctionFormData;
    onChange: (data: AuctionFormData) => void;
}> = ({ data, onChange }) => {
    const { currencies } = useReferenceData();
    return (
        <div className="extended-form auction-form">
            <div className="form-header">
                <div className="form-icon">🏛️</div>
                <div>
                    <h4 className="form-title">Auction Details</h4>
                    <p className="form-subtitle">Required for auction status</p>
                </div>
            </div>

            <div className="form-stack">
                <div>
                    <label className="form-label">
                        Auction House <span className="required">*</span>
                    </label>
                    <input
                        type="text"
                        value={data.auctionHouse}
                        onChange={(e) => onChange({ ...data, auctionHouse: e.target.value })}
                        placeholder="Sotheby's, Christie's, etc."
                        className="form-input"
                        required
                    />
                </div>

                <div className="form-grid">
                    <div>
                        <label className="form-label">
                            Proposed Sale Date <span className="required">*</span>
                        </label>
                        <input
                            type="date"
                            value={data.proposedSaleDate}
                            onChange={(e) => onChange({ ...data, proposedSaleDate: e.target.value })}
                            className="form-input"
                            required
                        />
                    </div>

                    <div>
                        <label className="form-label">
                            Lot Number
                        </label>
                        <input
                            type="text"
                            value={data.lotNumber}
                            onChange={(e) => onChange({ ...data, lotNumber: e.target.value })}
                            placeholder="e.g., LOT 123"
                            className="form-input"
                        />
                    </div>
                </div>

                <div>
                    <label className="form-label">
                        Auction Title
                    </label>
                    <input
                        type="text"
                        value={data.title}
                        onChange={(e) => onChange({ ...data, title: e.target.value })}
                        placeholder="Title of auction or sale"
                        className="form-input"
                    />
                </div>

                <div className="form-grid">
                    <div>
                        <label className="form-label">
                            Reserve Price
                        </label>
                        <div className="input-with-prefix">
                            <span className="input-prefix">$</span>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={data.reservePrice || ''}
                                onChange={(e) => onChange({
                                    ...data,
                                    reservePrice: e.target.value ? parseFloat(e.target.value) : null
                                })}
                                placeholder="0.00"
                                className="form-input"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="form-label">
                            Currency <span className="required">*</span>
                        </label>
                        <select
                            value={data.currency}
                            onChange={(e) => onChange({ ...data, currency: e.target.value })}
                            className="form-input"
                            required
                        >
                            {currencies?.map((curr: any) => (
                                <option key={curr.currencyCode} value={curr.currencyCode}>
                                    {curr.currencyCode}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ===== Main Component =====
export default function ArtworkStatusPanel({
    artworkId,
    onStatusChanged,
    groupByCategory = true,
    refresh,
}: ArtworkStatusPanelProps) {
    const { activeClientId, getAccessToken, isAdmin, isOwner, isValuer, activeClientLicense } = useUser();
    const isViewOnly = !activeClientLicense || activeClientLicense?.licenseExpired || !activeClientLicense?.canAddArtworks;
    const canEditArtwork = (isAdmin || isOwner || isValuer) && !isViewOnly;

    const clientIdRef = React.useRef<number | null>(null);

    React.useEffect(() => {
        clientIdRef.current = activeClientId;
    }, [activeClientId]);

    const api = React.useMemo(
        () =>
            createApiClient(
                { getAccessToken, getClientId: () => clientIdRef.current },
                { timeoutMs: 12000 }
            ),
        [getAccessToken]
    );

    const { statuses, current, history, loading, saving, error, updateStatus, reload } =
        useArtworkStatus(api, artworkId, { noOpIfUnchanged: true, retry: 2 });

    const [selectedCode, setSelectedCode] = useState<string>("");
    const [userTouched, setUserTouched] = useState(false);
    const [notes, setNotes] = useState("");
    const [additionalData, setAdditionalData] = useState<AdditionalFormData>(null);
    const [selectedHistoryItem, setSelectedHistoryItem] = useState<any>(null);
    const [showHistoryModal, setShowHistoryModal] = useState(false);

    const selectedStatus = useMemo(() =>
        statuses.find(s => s.code === selectedCode),
        [selectedCode, statuses]
    );

    const getInitialFormData = (extendedInfoType: string | null): AdditionalFormData => {
        const today = new Date().toISOString().split('T')[0];

        switch (extendedInfoType) {
            case 'VALUE':
                return { who: '', date: today, value: null, currency: 'USD' };
            case 'OUT':
                return { to: '', dateFrom: today, dateTo: '' };
            case 'FROM':
                return { fromWho: '', dateExpected: '', dateToReturn: '' };
            case 'AUCT':
                return {
                    auctionHouse: '',
                    proposedSaleDate: '',
                    lotNumber: '',
                    title: '',
                    reservePrice: null,
                    currency: 'USD'
                };
            default:
                return null;
        }
    };

    useEffect(() => {
        if (selectedStatus) {
            setAdditionalData(getInitialFormData(selectedStatus.extendedInfoType));
        } else {
            setAdditionalData(null);
        }
    }, [selectedStatus]);

    useEffect(() => {
        const refreshStatusDetails = async () => {
            await reload;
        }

        if (!loading) {
            refreshStatusDetails();
        }
    }, [refresh.status]);

    const groupedStatuses = useMemo(() => {
        const sortedStatuses = [...statuses].sort((a, b) => a.sortOrder - b.sortOrder);
        const map: Record<string, typeof sortedStatuses> = {};
        for (const s of sortedStatuses) {
            (map[s.category] ||= []).push(s);
        }
        return map;
    }, [statuses]);

    const handleUpdateStatus = async () => {
        if (!selectedCode) return;

        const request: SetStatusRequest = {
            statusCode: selectedCode,
            notes: notes || null,
            additionalData: additionalData || undefined
        };

        await updateStatus(request);
        if (!error) {
            setSelectedCode("");
            setNotes("");
            setAdditionalData(null);
            setUserTouched(false);
        }
    };

    const getStatusIcon = (extendedInfoType: string | null) => {
        switch (extendedInfoType) {
            case 'VALUE': return '💰';
            case 'OUT': return '📤';
            case 'FROM': return '📥';
            case 'AUCT': return '🏛️';
            default: return '';
        }
    };

    const getStatusColor = (statusCode: string) => {
        const colors: Record<string, string> = {
            AVAIL: 'status-badge-available',
            SOLD: 'status-badge-sold',
            HOLD: 'status-badge-reserved',
            NFS: 'status-badge-not-for-sale',
            REST: 'status-badge-restoration',
            LOAN_OUT: 'status-badge-on-loan'
        };
        return colors[statusCode] || 'status-badge-default';
    };

    const formatAdditionalDataPreview = (data: any) => {
        if (!data) return null;

        if (data.value) return `$${data.value.toLocaleString()} ${data.currency}`;
        if (data.to) return `To: ${data.to}`;
        if (data.fromWho) return `From: ${data.fromWho}`;
        if (data.auctionHouse) return `Auction: ${data.auctionHouse}`;

        return 'View details';
    };

    if (loading) {
        return (
            <div className="artwork-status-panel">
                <div className="loading-container">
                    <div className="loading-pulse"></div>
                    <div className="loading-pulse wide"></div>
                    <div className="form-grid">
                        <div className="loading-pulse"></div>
                        <div className="loading-pulse double"></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <ThemeProvider theme={theme}>
        <Paper elevation={3} square={false} sx={{ p: 2, width: '100%', boxSizing: 'border-box' }}>
                <div className="section-header">
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Artwork Status</Typography>
                    </Box>
                </div>
            <Divider sx={{ my: 2 }} />
            {/* Header */}
            <div className="panel-header">
                {current ? (
                    <div className="current-status">
                        <span className={`status-badge ${getStatusColor(current.statusCode)}`}>
                            {current.statusName}
                        </span>
                        <span className="status-date">
                            Last updated {new Date(current.statusDate).toLocaleDateString()}
                        </span>
                    </div>
                ) : (
                    <span className="no-status">No status set</span>
                )}
            </div>
            <Divider sx={{ my: 2 }} />
            {error && (
                <div className="error-message">
                    <svg className="error-icon" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    {error}
                </div>
            )}

            {/* Status Update Form */}

            {canEditArtwork && (
            <div className="embedded-layout">
                <h4 className="form-section-title">Update Status</h4>

                <div className="embedded-two-column">
                    {/* Status Selection */}
                    <div className="embedded-column">
                        <label className="form-label">Select New Status</label>
                        <select
                            value={selectedCode || ""}
                            onChange={(e) => {
                                setUserTouched(true);
                                setSelectedCode(e.target.value);
                            }}
                            className="embedded-select"
                        >
                            <option value="">Choose a status</option>
                            {Object.entries(groupedStatuses).map(([category, items]) => (
                                <optgroup key={category} label={category}>
                                    {items.map((s) => (
                                        <option key={s.artworkStatusId} value={s.code}>
                                            {s.name} {getStatusIcon(s.extendedInfoType)}
                                        </option>
                                    ))}
                                </optgroup>
                            ))}
                        </select>

                        {selectedStatus?.extendedInfoType != "NONE" && selectedStatus?.extendedInfoType != null && (
                            <div className="form-info">
                                <svg className="info-icon" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                                Additional information required
                            </div>
                        )}
                    </div>

                    {/* Notes and Forms */}
                    <div className="embedded-column">
                        <div>
                            <label className="form-label">Notes & Comments</label>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                rows={3}
                                placeholder="Optional notes (max 300 characters)"
                                className="notes-textarea"
                                maxLength={300}
                            />
                        </div>
                    </div>

                </div>

                {selectedStatus?.extendedInfoType != "NONE" && selectedStatus?.extendedInfoType != null && (
                    <div className="embedded-full-width">
                        {/* Dynamic Extended Info Forms */}
                        {selectedStatus?.extendedInfoType === 'VALUE' && additionalData && (
                            <ValueForm
                                data={additionalData as ValueFormData}
                                onChange={setAdditionalData}
                            />
                        )}
                        {selectedStatus?.extendedInfoType === 'OUT' && additionalData && (
                            <OutForm
                                data={additionalData as OutFormData}
                                onChange={setAdditionalData}
                            />
                        )}
                        {selectedStatus?.extendedInfoType === 'FROM' && additionalData && (
                            <FromForm
                                data={additionalData as FromFormData}
                                onChange={setAdditionalData}
                            />
                        )}
                        {selectedStatus?.extendedInfoType === 'AUCT' && additionalData && (
                            <AuctionForm
                                data={additionalData as AuctionFormData}
                                onChange={setAdditionalData}
                            />
                        )}
                    </div>
                )}
                        {/* Action Button */}
                    <div className="embedded-button-bar">
                            <button
                                type="button"
                                onClick={handleUpdateStatus}
                                disabled={!selectedCode || saving}
                                className="responsive-button"
                            >
                                {saving ? (
                                    <>
                                        <svg className="spinner" fill="none" viewBox="0 0 24 24">
                                            <circle className="spinner-circle" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="spinner-path" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Updating...
                                    </>
                                ) : (
                                    `Update to ${selectedStatus?.name || 'Status'}`
                                )}
                            </button>
                        </div>

            </div>
            )}

            {/* History Table */}
            <div className="history-section">
                <div className="history-header">
                    <h4 className="history-title">Status History</h4>
                    <span className="history-count">{history.length} records</span>
                </div>

                {history.length === 0 ? (
                    <div className="empty-history">
                        <svg className="empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="empty-text">No status history yet</p>
                        <p className="empty-subtext">Update the status to see history here</p>
                    </div>
                ) : (
                    <div className="history-table-container">
                        <table className="history-table">
                            <thead className="table-header">
                                <tr>
                                    <th key="Date" className="table-head">Date & Time</th>
                                    <th key="Status" className="table-head">Status</th>
                                    <th key="Changed By" className="table-head">Changed By</th>
                                    <th key="View" className="table-head">View</th>
                                </tr>
                            </thead>
                            <tbody className="table-body">
                                {history.map((h) => (
                                    <tr
                                        key={h.artworkStatusHistoryId}
                                        className="table-row"
                                        
                                    >
                                        <td key="Date" data-label="Date" className="table-cell">
                                            <div className="date-primary">{new Date(h.statusDate).toLocaleDateString()}</div>
                                            <div className="date-secondary">{new Date(h.statusDate).toLocaleTimeString()}</div>
                                        </td>
                                        <td key="Status"  data-label="Status" className="table-cell">
                                            <span className={`status-badge ${getStatusColor(h.statusCode)}`}>
                                                {h.statusName}
                                            </span>
                                        </td>
                                        <td key="Changed By"  data-label="Changed By" className="table-cell">
                                            {h.changedBy || ""}
                                        </td>
                                        <td key="View" data-label="View" className="table-cell">
                                            <Tooltip title="View details">
                                                <IconButton edge="end" onClick={() => {
                                                    setSelectedHistoryItem(h);
                                                    setShowHistoryModal(true);
                                                }}>
                                                    <DescriptionIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* History Modal */}
            <StatusViewer
                historyItem={selectedHistoryItem}
                isOpen={showHistoryModal}
                onClose={() => setShowHistoryModal(false)}
            />
            </Paper>
        </ThemeProvider>
    );
}